import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import CustomButton from '../../components/CustomButton';
import InputField from '../../components/ui/InputField';
import SelectField from '../../components/ui/SelectField';
import TextareaField from '../../components/ui/TextareaField';
import type {
    ProductCategory,
    ProductCategoryConfig,
} from '../../api/productCategory/productCategoryApiSlice';
import {
    ENTRY_SEQUENCES,
    HEAD_COUNT_LABELS,
    IDENTIFICATIONS,
    INHERIT,
    RATE_MODE_LEGACY,
    USAGE_PATTERNS,
    chargingPatch,
    directBookingPrerequisite,
    draftFromConfig,
    draftFromStandard,
    draftPaymentInvalid,
    formModeOf,
    hasCategoryNote,
    hasEventTypes,
    hasRateUnits,
    hasUsagePatterns as hasUsagePatternsUseCase,
    overrideStartingPoint,
    rateTimesInvalid,
    resetDraft,
    usagePatternChoices,
    usagePatternOf,
} from './categoryConfigRules';
import type {
    CategoryConfigDraft,
    InheritedValues,
    StandardValues,
} from './categoryConfigRules';
import { MAX_MIN_NIGHTS, bufferUnitFor, hasMinStay, toUnit } from './bookingWindowRules';
import { standardEventTypes } from '../../utils';
import {
    RESERVATION_FLOWS,
    channelsForFlow,
    prerequisiteHintKey,
    unmetPrerequisiteForChannel,
} from './serviceConfigurationRules';
import type { DepositedPrerequisites, Prerequisite } from './serviceConfigurationRules';
import type {
    TenantInvoiceIssuer,
    TenantPaymentChannel,
    TenantReservationFlow,
} from '../../api/account/accountApiTypes';
import { paths } from '../../routes';
import { CancellationSection } from './CancellationField';
import { cancellationBlocksSave } from './cancellationRules';

/** Where in Account a missing prerequisite is deposited — the section itself;
 *  `TAB_BY_ANCHOR` in `AccountPage` selects the owning tab first. */
const ACCOUNT_ANCHOR: Record<Prerequisite, string> = {
    stripe: 'payment-provider',
    bank_details: 'bank-details',
    invoice_issuance: 'invoice-issuer',
};

interface Props {
    category: ProductCategory;
    /** The values of this use case's standard product category — shown on
     *  every field left on "inherits", never written into the draft. */
    inherited: InheritedValues;
    /**
     * Present exactly when `category` IS the standard product category
     * (ADR-0026 §AE): the form then shows concrete values, with nothing to
     * inherit from, and saves a complete booking answer (§AF).
     */
    standard?: StandardValues;
    /** What the tenant has deposited in Account — a choice it cannot operate
     *  is shown with the reason and a link, not merely refused on save. */
    deposited: DepositedPrerequisites;
    /** WHO issues invoices, so the invoice channel can say so. */
    invoiceIssuer?: TenantInvoiceIssuer | null;
    saving: boolean;
    onCancel: () => void;
    /** The category's configuration, and its (possibly changed) name. */
    onSave: (config: ProductCategoryConfig, name: string) => void;
}

/**
 * One category's booking-flow configuration (ADR-0026 §B).
 *
 * ── "Inherits" is a VISIBLE, CHOOSABLE state — shown as its VALUE ─────────
 *
 * Every field offers it explicitly, and it is the default. That is not a
 * cosmetic choice: a category's configuration is an override set, so "not
 * overridden" has to be expressible in the form, or an operator could set an
 * override and never take it back. It also has to be *visible*, because a
 * blank field would otherwise read as "off" — and "off" and "inherits" are
 * different answers that produce different bookings.
 *
 * Since ADR-0026 §S it is rendered as the inherited value itself — the lead
 * time, the mode, the list — with "Standardwert" underneath, because the
 * operator wants to know what the value IS, not where it comes from. The
 * value is displayed and never copied into the draft; only switching a field
 * to an own value starts the override from it (`overrideStartingPoint`).
 *
 * The three booking-window numbers are one unit for the same reason: the
 * backend resolves the block, not the individual fields, so offering three
 * separate inherit switches would promise a mix it cannot store.
 */

/**
 * ADR-0026 §X: the usage-pattern preset. It fills the fields it names (entry
 * order, head count, sign-in, event input), and what is saved is those
 * fields: the pattern is READ from them, never stored. "Custom" when they
 * were set individually; choosing a pattern then overwrites them, and a line
 * says so, because those fields sit further down.
 */
const UsagePatternField = ({
    draft,
    storedDraft,
    useCase,
    patch,
    saving,
}: {
    draft: CategoryConfigDraft;
    storedDraft: CategoryConfigDraft;
    useCase: string;
    patch: (next: Partial<CategoryConfigDraft>) => void;
    saving: boolean;
}) => {
    const { t } = useTranslation();
    const label = (key: string) => t(`services.category_config.${key}`);
    const [applied, setApplied] = useState(false);
    const pattern = usagePatternOf(draft, useCase);
    const choose = (value: string) => {
        if (value !== 'oneOff' && value !== 'serial') return;
        const choices = usagePatternChoices(value, useCase);
        setApplied(true);
        // As switching to free text by hand does: the hidden list goes back
        // to what is STORED, so a hidden field cannot fail the save.
        patch(choices.eventTypeInput === 'freeText'
            ? { ...choices, eventTypes: storedDraft.eventTypes, eventTypesText: storedDraft.eventTypesText }
            : choices);
    };
    return (
        <>
            <SelectField
                label={label('usage_pattern')}
                labelAbove={true}
                help={label('usage_pattern_help')}
                value={pattern}
                onChange={choose}
                options={pattern === 'custom' ? [...USAGE_PATTERNS, 'custom'] : [...USAGE_PATTERNS]}
                optionLabels={{
                    oneOff: label('usage_pattern_one_off'),
                    serial: label('usage_pattern_serial'),
                    custom: label('usage_pattern_custom'),
                }}
                disabled={saving}
            />
            {applied && (
                <p className={'-mt-2 text-xs text-foreground/60'} role={'status'}>
                    {label(hasEventTypes(useCase)
                        ? 'usage_pattern_applied_event'
                        : 'usage_pattern_applied')}
                </p>
            )}
        </>
    );
};

/**
 * Check-in on the STANDARD category: the use case's own flow, on or off
 * (ADR-0026 §AF). On is offered only where there is a flow to switch on — the
 * server refuses the rest (§J's empty-flow screen) — and otherwise the field
 * says why, with the way to the tab that authors it.
 */
const StandardCheckIn = ({ standard, value, onChange, saving }: {
    standard: StandardValues;
    value: string;
    onChange: (value: string) => void;
    saving: boolean;
}) => {
    const { t } = useTranslation();
    const label = (key: string) => t(`services.category_config.${key}`);
    const canSwitchOn = standard.checkInAuthored || value === 'on';
    return (
        <div className={'flex flex-col gap-1'}>
            <SelectField
                label={label('check_in')}
                labelAbove={true}
                help={label('check_in_help_standard')}
                value={value}
                onChange={onChange}
                options={canSwitchOn ? ['on', 'off'] : ['off']}
                optionLabels={{
                    on: label('check_in_on'),
                    off: label('check_in_off_value'),
                }}
                disabled={saving}
            />
            {!standard.checkInAuthored && (
                <span className={'text-xs text-foreground/60'}>
                    {label('check_in_not_authored')}{' '}
                    <Link
                        to={`${paths.rentalProducts}#check-in-checkout`}
                        className={'text-primary underline'}
                    >
                        {t('services.configuration.usage_flow_link')}
                    </Link>
                </span>
            )}
        </div>
    );
};

const CategoryConfigForm = ({
    category,
    inherited,
    standard,
    deposited,
    invoiceIssuer,
    saving,
    onCancel,
    onSave,
}: Props) => {
    const { t, i18n } = useTranslation();
    const isStandard = !!standard;
    const mode = formModeOf(isStandard);

    // The form as stored — what a hidden field falls back to.
    const storedDraft = useMemo(
        () => (standard
            ? draftFromStandard(
                standard.entry, category.config, standard.checkInEnabled, category.useCase,
            )
            : draftFromConfig(category.config, category.useCase)),
        [standard, category.config, category.useCase],
    );
    const [draft, setDraft] = useState<CategoryConfigDraft>(storedDraft);
    const [wasReset, setWasReset] = useState(false);

    const supportsEventTypes = hasEventTypes(category.useCase);
    const supportsNote = hasCategoryNote(category.useCase);
    const supportsEventTypeInput = hasEventTypes(category.useCase);
    // With a free-text name there is no list to choose from, so the list
    // settings step aside. A configured list is kept, not cleared: switching
    // back to the dropdown restores it.
    const showEventTypes = supportsEventTypes && draft.eventTypeInput !== 'freeText';
    const supportsRateUnits = hasRateUnits(category.useCase);
    const showUsagePatterns = hasUsagePatternsUseCase(category.useCase);
    // Days for a whole-day use case, minutes otherwise — the server refuses a
    // lead time that is not a whole number of days for `apartment`/`venue`.
    const unitLabel = t(
        `services.configuration.booking_window_unit_${bufferUnitFor(category.useCase)}`,
    );

    const patch = (next: Partial<CategoryConfigDraft>) =>
        setDraft((current) => ({ ...current, ...next }));


    // The name is edited here too, with everything else about the category —
    // one edit action rather than a rename beside a settings button. The key
    // is not: it is fixed once chosen, because configuration points at it.
    const [name, setName] = useState(category.name);

    const standardList = useMemo(
        () => standardEventTypes(i18n.language),
        [i18n.language],
    );

    /** Switching one field to an own value starts it from the standard. */
    const startOverride = (field: 'payment' | 'bookingWindow' | 'eventTypes') =>
        patch(overrideStartingPoint(field, inherited, category.useCase, standardList));

    /**
     * ALL THREE numbers are required once an own window is chosen — a blank is
     * refused rather than quietly filled.
     *
     * The server stores the window as a COMPLETE block: `resolveBookingWindow`
     * fills any field the request omits from the documented per-use-case
     * default, NOT from whatever this tenant configured on its use case. So a
     * category overriding only the horizon would silently acquire a different
     * lead time than the use case it belongs to, and nothing on screen would
     * have said so.
     *
     * Requiring all three makes the stored block exactly what the operator
     * typed. Switching to an own window prefills all three from the standard
     * (`overrideStartingPoint`), so the requirement only bites when the
     * operator clears one.
     */
    const windowInvalid = useMemo(
        () =>
            draft.bookingWindow === 'override'
            && ([draft.leadMinutes, draft.horizonMonths, draft.followUpMinutes].some(
                (raw) => !/^\d+$/.test(raw.trim()),
            )
            // Blank is allowed (one night); a value must be a whole number
            // between 1 and the cap the server enforces.
            || (hasMinStay(category.useCase)
                && draft.minNights.trim() !== ''
                && (!/^[1-9]\d*$/.test(draft.minNights.trim())
                    || Number(draft.minNights.trim()) > MAX_MIN_NIGHTS))),
        [draft, category.useCase],
    );

    const paymentInvalid = draftPaymentInvalid(draft, isStandard);
    const rateTimeInvalid = rateTimesInvalid(draft);
    const cancellationInvalid = cancellationBlocksSave(draft.cancellation, category.useCase, isStandard);

    const label = (key: string) => t(`services.category_config.${key}`);

    // ── How the inherited value reads (ADR-0026 §S) ──────────────────────
    const inheritOption = (value: string) =>
        t('services.category_config.inherit_value', { value });
    // The standard category IS the standard: no caption on its own values.
    const standardCaption = isStandard ? null : (
        <span className={'text-xs text-foreground/60'}>
            {inherited.saved ? label('standard_value') : label('standard_value_unsaved')}
        </span>
    );

    /** The link a blocked choice offers — "deposit this in Account first",
     *  worded for the channel it blocks (`prerequisiteHintKey`). */
    const prerequisiteHint = (prerequisite: Prerequisite, channel?: TenantPaymentChannel) => (
        <span className={'block text-xs text-foreground/60'}>
            {t(prerequisiteHintKey(prerequisite, channel))}{' '}
            <Link
                to={`${paths.account}#${ACCOUNT_ANCHOR[prerequisite]}`}
                className={'text-primary underline'}
            >
                {t('services.configuration.prerequisite_link')}
            </Link>
        </span>
    );

    const directBookingUnmet = directBookingPrerequisite(draft, inherited, deposited);
    const changeCharging = (value: string) => patch(chargingPatch(value, draft, standard));

    const applyReset = () => {
        const reset = resetDraft(category.useCase, standard);
        // A further category's own rule must be CLEARED by the save, not
        // left out — an omitted field keeps what is stored (M10).
        // And a reset is a change: the standard category then sends "no rule"
        // rather than leaving the field out (which keeps the stored rule).
        setDraft({
            ...reset,
            cancellation: { ...reset.cancellation, stored: draft.cancellation.stored, touched: true },
        });
        setWasReset(true);
    };
    const chargingText = t(`services.configuration.charging_${inherited.charging}`);
    const directBookingText = inherited.directBooking
        ? label('direct_booking_on')
        : label('direct_booking_off');
    const checkInText = inherited.checkIn ? label('check_in_on') : label('check_in_off_value');
    const paymentText = (() => {
        if (inherited.charging === 'free') return label('payment_none_free');
        if (!inherited.payment || inherited.payment.channels.length === 0) {
            return t('common.missing');
        }
        const channels = inherited.payment.channels
            .map((channel) => t(`services.configuration.channel_${channel}`))
            .join(', ');
        const flow = t(`services.configuration.flow_${inherited.payment.flow}`);
        return `${flow} · ${channels}`;
    })();
    const eventTypesText = (inherited.eventTypes ?? standardList).join(', ');
    // On the standard category "inherits" is our standard list itself — the
    // stored absence; on a further one it is the standard category's list.
    let eventTypesInheritLabel = label('event_types_standard');
    let eventTypesShown = standardList.join(', ');
    if (!isStandard) {
        eventTypesShown = eventTypesText;
        eventTypesInheritLabel = inheritOption(inherited.eventTypes
            ? label('event_types_inherited_own')
            : label('event_types_standard'));
    }
    const minStayLabel = t('services.configuration.booking_window_minNights_label');
    const nightsLabel = t('services.configuration.booking_window_unit_nights');
    const windowText = t('services.category_config.window_summary', {
        lead: toUnit(inherited.bookingWindow.leadMinutes, category.useCase),
        horizon: inherited.bookingWindow.horizonMonths,
        followUp: toUnit(inherited.bookingWindow.followUpMinutes, category.useCase),
        unit: unitLabel,
    }) + (hasMinStay(category.useCase)
        ? ` · ${t('services.category_config.window_summary_min_stay', {
            count: inherited.bookingWindow.minNights,
        })}`
        : '');

    return (
        <div
            className={'mt-3 flex flex-col gap-4 rounded-[5px] border border-foreground/10 p-4'}
        >
            <InputField
                label={t('services.product_categories.name')}
                labelAbove={true}
                value={name}
                onChange={setName}
                disabled={saving}
            />

            <p className={'text-xs text-foreground/60'}>
                {label(mode.introKey)}
            </p>

            {isStandard && !standard?.saved && (
                <p className={'text-xs text-foreground/60 rounded-[5px] border'
                    + ' border-foreground/10 bg-foreground/5 px-3 py-2'}
                >
                    {label('standard_unsaved')}
                </p>
            )}

            {/* ADR-0026 §X: first, because it sets fields in several
                sections below. */}
            {showUsagePatterns && (
                <UsagePatternField
                    draft={draft}
                    storedDraft={storedDraft}
                    useCase={category.useCase}
                    patch={patch}
                    saving={saving}
                />
            )}

            {/* First, because it is the one a tenant actually comes here to
                change: check-in for the meeting rooms, none for the barbecue
                site. Two states only — switching one ON per category needs the
                use-case editor's whole authoring surface. */}
            {standard ? (
                <StandardCheckIn
                    standard={standard}
                    value={draft.checkIn}
                    onChange={(value) => patch({ checkIn: value })}
                    saving={saving}
                />
            ) : (
                <SelectField
                    label={label('check_in')}
                    labelAbove={true}
                    help={label('check_in_help')}
                    value={draft.checkIn}
                    onChange={(value) => patch({ checkIn: value })}
                    options={[INHERIT, 'off']}
                    optionLabels={{
                        [INHERIT]: inheritOption(checkInText),
                        off: label('check_in_off'),
                    }}
                    disabled={saving}
                />
            )}
            {draft.checkIn === INHERIT && standardCaption}

            <SelectField
                label={label('charging')}
                labelAbove={true}
                help={label('charging_help')}
                value={draft.charging}
                onChange={changeCharging}
                options={mode.chargingOptions}
                optionLabels={{
                    [INHERIT]: inheritOption(chargingText),
                    // The use-case card's labels — one wording for one choice.
                    free: t('services.configuration.charging_free'),
                    mixed: t('services.configuration.charging_mixed'),
                    chargeable: t('services.configuration.charging_chargeable'),
                }}
                disabled={saving}
            />
            {draft.charging === INHERIT && standardCaption}

            <SelectField
                label={label('direct_booking')}
                labelAbove={true}
                help={label('direct_booking_help')}
                value={draft.directBooking}
                onChange={(value) => patch({ directBooking: value })}
                options={mode.directBookingOptions}
                optionLabels={{
                    [INHERIT]: inheritOption(directBookingText),
                    true: label('direct_booking_on'),
                    false: label('direct_booking_off'),
                }}
                disabled={saving}
            />
            {draft.directBooking === INHERIT && standardCaption}
            {directBookingUnmet && (
                <span className={'flex flex-col'}>
                    <span className={'text-xs text-foreground/60'}>
                        {t('services.configuration.direct_booking_requires')}
                    </span>
                    {prerequisiteHint(directBookingUnmet)}
                </span>
            )}

            {/* The arrangement, offered only as a WHOLE.

                A flow without a channel is not a half-configured arrangement
                the server could complete — it is one it refuses, so the form
                sends nothing until both halves are answered. Which channels a
                flow admits comes from `channelsForFlow`, the same rule the
                per-use-case form and the endpoint use; this component holds
                markup, not a second copy of the rule.

                Prerequisites (a connected Stripe account, deposited bank
                details) are NOT re-checked here. The endpoint refuses a channel
                whose prerequisite is missing with a sentence naming it, and
                `errorMessageFrom` shows the server's own message — so the
                operator reads the real reason rather than a guess this form
                would have to keep in step. */}
            <div className={'flex flex-col gap-2'}>
                {isStandard && draft.charging === 'free' && (
                    <span className={'flex flex-col'}>
                        <span className={'text-sm text-foreground'}>{label('payment')}</span>
                        <span className={'text-xs text-foreground/60'}>
                            {label('payment_none_free')}
                        </span>
                    </span>
                )}
                {!isStandard && (
                    <SelectField
                        label={label('payment')}
                        labelAbove={true}
                        help={label('payment_help')}
                        value={draft.payment}
                        onChange={(value) =>
                            value === INHERIT
                                ? patch({ payment: value, paymentFlow: '', paymentChannels: [] })
                                : startOverride('payment')
                        }
                        options={[INHERIT, 'override']}
                        optionLabels={{
                            [INHERIT]: inheritOption(paymentText),
                            override: label('payment_own'),
                        }}
                        disabled={saving}
                    />
                )}
                {draft.payment === INHERIT && standardCaption}
                {draft.payment === 'override' && !(isStandard && draft.charging === 'free') && (
                    <div className={'flex flex-col gap-2'}>
                        <SelectField
                            label={label(mode.paymentFlowLabelKey)}
                            labelAbove={true}
                            help={mode.paymentFlowHelpKey && label(mode.paymentFlowHelpKey)}
                            value={draft.paymentFlow}
                            // Changing the flow clears the channels: the two
                            // admissible sets are disjoint (ADR-0016 Amendment
                            // 6), so a kept selection would be one the endpoint
                            // refuses.
                            onChange={(value) =>
                                patch({ paymentFlow: value, paymentChannels: [] })
                            }
                            // No '' option: "nothing chosen yet" is the
                            // placeholder, which `SelectField` shows for an
                            // empty value.
                            options={[...RESERVATION_FLOWS]}
                            placeholder={label('payment_flow')}
                            optionLabels={Object.fromEntries(
                                RESERVATION_FLOWS.map((flow) => [
                                    flow,
                                    t(`services.configuration.flow_${flow}`),
                                ]),
                            )}
                            disabled={saving}
                        />
                        {draft.paymentFlow !== '' && (
                            <div className={'flex flex-col gap-1'}>
                                <span className={'text-sm text-foreground/60'}>
                                    {t('services.configuration.channels')}
                                </span>
                                <span className={'text-xs text-foreground/50'}>
                                    {label('payment_channels_help')}
                                </span>
                                {channelsForFlow(
                                    draft.paymentFlow as TenantReservationFlow,
                                ).map((channel) => {
                                    const unmet = unmetPrerequisiteForChannel(
                                        channel as TenantPaymentChannel, deposited,
                                    );
                                    const checked = draft.paymentChannels.includes(channel);
                                    // Who issues the invoice is a tenant-level
                                    // answer (ADR-0012), shown where it matters.
                                    const issuerNote = channel === 'invoice' && !unmet && invoiceIssuer
                                        ? t(`services.configuration.invoice_issued_by_${invoiceIssuer}`)
                                        : null;
                                    return (
                                        <label
                                            key={channel}
                                            className={'flex items-start gap-2 text-sm text-foreground'}
                                        >
                                            <input
                                                type={'checkbox'}
                                                className={'mt-1'}
                                                checked={checked}
                                                onChange={(e) =>
                                                    patch({
                                                        paymentChannels: e.target.checked
                                                            ? [...draft.paymentChannels, channel]
                                                            : draft.paymentChannels.filter(
                                                                (c) => c !== channel,
                                                            ),
                                                    })
                                                }
                                                // A checked one stays uncheckable,
                                                // so a stale choice can be removed.
                                                disabled={saving || (!!unmet && !checked)}
                                            />
                                            <span>
                                                {t(`services.configuration.channel_${channel}`)}
                                                {unmet && prerequisiteHint(unmet, channel as TenantPaymentChannel)}
                                                {issuerNote && (
                                                    <span className={'block text-xs text-foreground/60'}>
                                                        {issuerNote}
                                                    </span>
                                                )}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
                {paymentInvalid && (
                    <p className={'text-xs text-error'}>{label('payment_incomplete')}</p>
                )}
            </div>

            {/* Venue-only, and said rather than silently omitted: a missing
                field reads as a bug, an explained absence does not. The server
                refuses it for every other use case, so hiding it here would
                only move the surprise. */}
            {supportsEventTypeInput && (
                <SelectField
                    label={label('event_input')}
                    labelAbove={true}
                    help={label('event_input_help')}
                    value={draft.eventTypeInput}
                    onChange={(value) =>
                        value === 'freeText'
                            ? patch({
                                eventTypeInput: 'freeText',
                                // The list is hidden from here on but still
                                // saved (kept for switching back). Reset it to
                                // what is STORED, so an unsaved edit cannot be
                                // refused by the server with no visible field
                                // left to correct it in.
                                eventTypes: storedDraft.eventTypes,
                                eventTypesText: storedDraft.eventTypesText,
                            })
                            : patch({ eventTypeInput: 'select' })
                    }
                    options={['select', 'freeText']}
                    optionLabels={{
                        select: label('event_input_select'),
                        freeText: label('event_input_free_text'),
                    }}
                    disabled={saving}
                />
            )}

            {showEventTypes && (
                <div className={'flex flex-col gap-2'}>
                    <SelectField
                        label={label('event_types')}
                        labelAbove={true}
                        help={label('event_types_help')}
                        value={draft.eventTypes}
                        onChange={(value) =>
                            value === INHERIT
                                ? patch({ eventTypes: value })
                                : startOverride('eventTypes')
                        }
                        options={[INHERIT, 'override']}
                        optionLabels={{
                            // On the standard category "inherits" is our
                            // standard list itself — the stored absence.
                            [INHERIT]: eventTypesInheritLabel,
                            override: label('event_types_own'),
                        }}
                        disabled={saving}
                    />
                    {draft.eventTypes === INHERIT && (
                        <span className={'flex flex-col'}>
                            <span className={'text-sm text-foreground/80'}>
                                {eventTypesShown}
                            </span>
                            {standardCaption}
                        </span>
                    )}
                    {draft.eventTypes === 'override' && (
                        <InputField
                            label={label('event_types_list')}
                            labelAbove={true}
                            help={label('event_types_list_help')}
                            value={draft.eventTypesText}
                            onChange={(value) => patch({ eventTypesText: value })}
                            disabled={saving}
                        />
                    )}
                </div>
            )}
            {!supportsEventTypes && (
                <p className={'text-xs text-foreground/50'}>{label('event_types_na')}</p>
            )}

            <div className={'flex flex-col gap-2'}>
                {isStandard ? (
                    <span className={'flex flex-col'}>
                        <span className={'text-sm text-foreground'}>{label('booking_window')}</span>
                        <span className={'text-xs text-foreground/60'}>
                            {label('booking_window_help')}
                        </span>
                    </span>
                ) : (
                    <SelectField
                        label={label('booking_window')}
                        labelAbove={true}
                        help={label('booking_window_help')}
                        value={draft.bookingWindow}
                        onChange={(value) =>
                            value === INHERIT
                                ? patch({ bookingWindow: value })
                                : startOverride('bookingWindow')
                        }
                        options={[INHERIT, 'override']}
                        optionLabels={{
                            [INHERIT]: inheritOption(windowText),
                            override: label('booking_window_own'),
                        }}
                        disabled={saving}
                    />
                )}
                {draft.bookingWindow === INHERIT && standardCaption}
                {draft.bookingWindow === 'override' && (
                    <div className={'grid grid-cols-1 sm:grid-cols-3 gap-3'}>
                        <InputField
                            label={`${label('lead')} (${unitLabel})`}
                            labelAbove={true}
                            value={draft.leadMinutes}
                            onChange={(value) => patch({ leadMinutes: value })}
                            disabled={saving}
                        />
                        <InputField
                            label={label('horizon_months')}
                            labelAbove={true}
                            value={draft.horizonMonths}
                            onChange={(value) => patch({ horizonMonths: value })}
                            disabled={saving}
                        />
                        <InputField
                            label={`${label('follow_up')} (${unitLabel})`}
                            labelAbove={true}
                            value={draft.followUpMinutes}
                            onChange={(value) => patch({ followUpMinutes: value })}
                            disabled={saving}
                        />
                        {hasMinStay(category.useCase) && (
                            <InputField
                                label={`${minStayLabel} (${nightsLabel})`}
                                labelAbove={true}
                                value={draft.minNights}
                                onChange={(value) => patch({ minNights: value })}
                                disabled={saving}
                            />
                        )}
                    </div>
                )}
                {windowInvalid && (
                    <p className={'text-xs text-error'}>{label('numbers_only')}</p>
                )}
            </div>

            {/* M10: the cancellation rule — a booking field like the window
                above, so a further category inherits the standard category's
                rule. Rooms per category; station units on their standard
                category only; handover use cases have none (said, not hidden). */}
            <CancellationSection
                useCase={category.useCase}
                value={draft.cancellation}
                onChange={(cancellation) => patch({ cancellation })}
                isStandard={isStandard}
                inherited={inherited.cancellation}
                standardCaption={standardCaption}
                saving={saving}
            />

            {/* No "inherits" choice here, unlike every field above: there is
                no use-case note behind it. Empty means the booking screen shows
                no note at all. Same DE/EN pair as an object's "Wichtiger
                Hinweis", and venue-only for the same reason event types are. */}
            {supportsNote ? (
                <div className={'flex flex-col gap-2'}>
                    <span className={'text-xs text-foreground/50'}>{label('note_help')}</span>
                    <TextareaField
                        label={`${label('note')} (DE)`}
                        labelAbove={true}
                        value={draft.noteDe}
                        onChange={(value) => patch({ noteDe: value })}
                        rows={2}
                        disabled={saving}
                    />
                    <TextareaField
                        label={`${label('note')} (EN)`}
                        labelAbove={true}
                        value={draft.noteEn}
                        onChange={(value) => patch({ noteEn: value })}
                        rows={2}
                        disabled={saving}
                    />
                </div>
            ) : (
                <p className={'text-xs text-foreground/50'}>{label('note_na')}</p>
            )}

            {/* #3035: how the customer's booking starts, and what the head
                count is called. Category-only like the note — no use case
                carries either — so the default is today's behaviour, not
                "inherits". */}
            {showUsagePatterns && (
                <div className={'flex flex-col gap-2'}>
                    <SelectField
                        label={label('entry_sequence')}
                        labelAbove={true}
                        help={label('entry_sequence_help')}
                        value={draft.entrySequence}
                        onChange={(value) => patch({ entrySequence: value })}
                        options={[...ENTRY_SEQUENCES]}
                        optionLabels={{
                            dateFirst: label('entry_sequence_date_first'),
                            objectFirst: label('entry_sequence_object_first'),
                        }}
                        disabled={saving}
                    />
                    <SelectField
                        label={label('head_count_label')}
                        labelAbove={true}
                        value={draft.headCountLabel}
                        onChange={(value) => patch({ headCountLabel: value })}
                        options={[...HEAD_COUNT_LABELS]}
                        optionLabels={{
                            guests: label('head_count_guests'),
                            participants: label('head_count_participants'),
                            members: label('head_count_members'),
                        }}
                        disabled={saving}
                    />
                    <SelectField
                        label={label('identification')}
                        labelAbove={true}
                        help={label('identification_help')}
                        value={draft.identification}
                        onChange={(value) => patch({ identification: value })}
                        options={[...IDENTIFICATIONS]}
                        optionLabels={{
                            optional: label('identification_optional'),
                            first: label('identification_first'),
                        }}
                        disabled={saving}
                    />
                </div>
            )}

            {/* #1713: the rate-unit knobs. These change what a booking COSTS,
                so the default is not "inherit" — there is no use-case value
                behind it — but "the previous calculation, unchanged". */}
            {supportsRateUnits && (
                <div className={'flex flex-col gap-2'}>
                    <span className={'text-xs text-foreground/50'}>
                        {label('rate_units_help')}
                    </span>
                    <SelectField
                        label={label('weekend_mode')}
                        labelAbove={true}
                        help={label('weekend_mode_help')}
                        value={draft.rateWeekendMode}
                        onChange={(value) => patch({ rateWeekendMode: value })}
                        options={[RATE_MODE_LEGACY, 'package', 'per_day']}
                        optionLabels={{
                            [RATE_MODE_LEGACY]: label('weekend_mode_legacy'),
                            package: label('weekend_mode_package'),
                            per_day: label('weekend_mode_per_day'),
                        }}
                        disabled={saving}
                    />
                    {draft.rateWeekendMode === 'package' && (
                        <InputField
                            label={label('weekend_start')}
                            labelAbove={true}
                            placeholder={'13:00'}
                            value={draft.rateWeekendStart}
                            onChange={(value) => patch({ rateWeekendStart: value })}
                            className={'max-w-[220px]'}
                            disabled={saving}
                        />
                    )}
                    {draft.rateWeekendMode !== RATE_MODE_LEGACY && (
                        <InputField
                            label={label('half_day_cutoff')}
                            labelAbove={true}
                            placeholder={'13:00'}
                            value={draft.rateHalfDayCutoff}
                            onChange={(value) => patch({ rateHalfDayCutoff: value })}
                            className={'max-w-[220px]'}
                            disabled={saving}
                        />
                    )}
                    {rateTimeInvalid && (
                        <p className={'text-xs text-error'}>{label('rate_time_shape')}</p>
                    )}
                </div>
            )}

            {wasReset && (
                <p className={'text-xs text-foreground/60'}>{label('reset_done')}</p>
            )}

            {/* On a phone the three buttons do not fit one row inside the
                card — "Speichern" was pushed out of it. They stack: save and
                cancel side by side in equal columns, the reset below at full
                width. From `sm` the original one-row layout returns. */}
            <div className={'flex flex-col-reverse gap-2 sm:flex-row sm:justify-between'}>
                {/* "Auf Standard zurücksetzen" (ADR-0026 §AJ) FILLS the form —
                    our template on the standard category, the standard
                    category's values on a further one. Saving commits it, so
                    Cancel still undoes it. */}
                <div className={'w-full sm:w-auto'}>
                    <CustomButton
                        title={label('reset')}
                        onClick={applyReset}
                        isFilled={false}
                        disabled={saving}
                        fullWidth={true}
                    />
                </div>
                <div className={'grid grid-cols-2 gap-2 w-full sm:w-auto'}>
                    <CustomButton
                        title={t('common.cancel')}
                        onClick={onCancel}
                        isFilled={false}
                        disabled={saving}
                        fullWidth={true}
                    />
                    <CustomButton
                        title={t('common.save')}
                        onClick={() => onSave(mode.toConfig(draft, category.useCase), name)}
                        isFilled={true}
                        loading={saving}
                        disabled={!name.trim() || windowInvalid || paymentInvalid || rateTimeInvalid
                            || cancellationInvalid}
                        fullWidth={true}
                    />
                </div>
            </div>
        </div>
    );
};

export default CategoryConfigForm;
